import { wochenBuchstabe } from '@famora/core/timetable/wochen'
import { datumLesen } from './datum'

export type Woche = 'A' | 'B'

export interface Fach {
  id: string
  name: string
  short: string | null
  color: string | null
  archived: boolean
}

export interface Stunde {
  id: string
  weekday: number
  week: Woche | null
  startsAt: string
  endsAt: string
  subjectId: string
  subject: { name: string; short: string | null; color: string | null } | null
  room: string | null
  note: string | null
}

export interface Stundenplan {
  id: string
  memberId: string
  name: string
  validFrom: string
  validUntil: string | null
  weekPattern: 'single' | 'ab'
  abAnchorMonday: string | null
  visibility: string
  ownerMemberId: string | null
  lessons?: Stunde[]
}

export const WOCHENTAG_NAME = [
  '',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
  'Sonntag',
] as const

/** Mo-Fr immer; Sa/So nur, wenn dort wirklich Unterricht steht. */
export function sichtbareTage(stunden: readonly Stunde[]): number[] {
  const tage = [1, 2, 3, 4, 5]
  if (stunden.some((s) => s.weekday === 6)) tage.push(6)
  if (stunden.some((s) => s.weekday === 7)) tage.push(7)
  return tage
}

/**
 * Stunden je Wochentag, nach Beginn sortiert. Mit Wochenbuchstabe werden nur
 * die Stunden dieser Woche und die ohne Buchstabe gezeigt; ohne (single-Plan)
 * alle.
 */
export function stundenProTag(
  stunden: readonly Stunde[],
  woche: Woche | null,
): Map<number, Stunde[]> {
  const karte = new Map<number, Stunde[]>()
  for (const s of stunden) {
    if (woche !== null && s.week !== null && s.week !== woche) continue
    const liste = karte.get(s.weekday) ?? []
    liste.push(s)
    karte.set(s.weekday, liste)
  }
  for (const liste of karte.values()) liste.sort((a, b) => a.startsAt.localeCompare(b.startsAt))
  return karte
}

export const zeitSpanne = (s: Pick<Stunde, 'startsAt' | 'endsAt'>): string =>
  `${s.startsAt}–${s.endsAt}`

const datumLang = (d: string): string =>
  datumLesen(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

export function gueltigkeitText(plan: Pick<Stundenplan, 'validFrom' | 'validUntil'>): string {
  return plan.validUntil
    ? `${datumLang(plan.validFrom)} – ${datumLang(plan.validUntil)}`
    : `ab ${datumLang(plan.validFrom)}`
}

/** Der Plan, der heute gilt - sonst der juengste (ein neues Schuljahr wird vorbereitet). */
export function aktuellerPlan<T extends Pick<Stundenplan, 'validFrom' | 'validUntil'>>(
  plaene: readonly T[],
  heute: string,
): T | undefined {
  const gueltig = plaene.filter(
    (p) => p.validFrom <= heute && (p.validUntil === null || p.validUntil >= heute),
  )
  if (gueltig.length > 0) return gueltig[0]
  return [...plaene].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0]
}

/** Welche Woche heute ist - NULL im single-Plan, der keine Wochen kennt. */
export function wocheHeute(
  plan: Pick<Stundenplan, 'weekPattern' | 'abAnchorMonday'>,
  heute: string,
): Woche | null {
  if (plan.weekPattern !== 'ab' || !plan.abAnchorMonday) return null
  return wochenBuchstabe(heute, plan.abAnchorMonday)
}

/** Was der Server fuer PUT /lessons erwartet - ohne id und ohne aufgeloestes Fach. */
export function alsStundenBody(stunden: readonly Stunde[]): Record<string, unknown>[] {
  return stunden.map((s) => ({
    weekday: s.weekday,
    week: s.week,
    startsAt: s.startsAt,
    endsAt: s.endsAt,
    subjectId: s.subjectId,
    room: s.room,
    note: s.note,
  }))
}
